import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import moment from "moment";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeftIcon, MailIcon } from "@heroicons/react/outline";

import { verifyEmailSchema, VerifyEmailFormValues } from "./schema";

import AuthSubmitButton from "@/components/AuthSubmitButton";
import { Button } from "@/components/ui/button";
import { OTPInput } from "@/components/ui/OTPInput";
import { useSendVerificationEmail } from "@/queries/auth/useSendVerificationEmail";
import { useVerifyEmailCode } from "@/queries/auth/useVerifyEmailCode";
import { useVerifyOAuthEmail } from "@/queries/auth/useVerifyOAuthEmail";
import { useAuth } from "@/context/auth-context";

export const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") as string;
  const pendingToken = searchParams.get("pending_token");
  const auth = useAuth();
  const [attempts, setAttempts] = useState(1);
  const [resendTimer, setResendTimer] = useState<number | null>(null);
  const { mutate: resendVerificationCode } = useSendVerificationEmail();
  const { mutate: verifyEmailCode, isPending: isVerifyEmailCodeLoading } = useVerifyEmailCode();
  const { mutate: verifyOAuthEmail, isPending: isVerifyOAuthEmailLoading } = useVerifyOAuthEmail();

  const provider = localStorage.getItem("last_login_type");

  const isLoading = pendingToken ? isVerifyOAuthEmailLoading : isVerifyEmailCodeLoading;
  const {
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid },
    setValue,
  } = useForm<VerifyEmailFormValues>({
    resolver: zodResolver(verifyEmailSchema),
    defaultValues: {
      code: "",
    },
    mode: "onSubmit",
  });

  useEffect(() => {
    const inputElement = document.querySelector('[data-tid="verify-email-input-0"]');

    if (inputElement) {
      (inputElement as HTMLInputElement).focus();
    }

    return () => {
      reset();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (resendTimer === null || resendTimer <= 0) {
      return;
    }

    const intervalId = setInterval(() => {
      setResendTimer(prevTimer => (prevTimer !== null && prevTimer > 0 ? prevTimer - 1 : null));
    }, 1000);

    return () => clearInterval(intervalId);
  }, [resendTimer]);

  const handleResendCode = () => {
    resendVerificationCode({
      email: email!,
    });

    setResendTimer(60 * attempts);
    setAttempts(prev => prev + 1);
  };

  const onSubmit = (data: VerifyEmailFormValues) => {
    if (pendingToken) {
      verifyOAuthEmail({
        code: data.code.trim(),
        pending_authentication_token: pendingToken?.trim(),
        provider: provider ?? "",
      });

      return;
    }

    verifyEmailCode({
      code: data.code,
      email: email!,
      ...(pendingToken && { pending_authentication_token: pendingToken }),
    });
  };

  const handleBackToLogin = () => {
    localStorage.removeItem("last_login_type");

    if (auth?.user) {
      auth.logout();
    }

    navigate("/login");
  };

  return (
    <div className="flex items-center justify-center">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-50">
            <MailIcon className="h-8 w-8 text-blue-600" strokeWidth={2} />
          </div>
        </div>

        <div className="mb-2 text-center">
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Check your email</h1>
        </div>

        <p className="mb-8 text-center text-base text-gray-600">
          {pendingToken
            ? "Please verify your email to complete login."
            : "We've sent a verification code to your email."}
          <br />
          <span className="mt-1 inline-block font-semibold text-gray-900">{email}</span>
        </p>

        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label className="mb-3 block text-center text-sm font-medium text-gray-700">
              Enter verification code
            </label>
            <OTPInput
              code={watch("code")}
              setCode={value =>
                setValue("code", value, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              onPaste={value => onSubmit({ code: value })}
              errorMessage={errors.code?.message}
            />
            <div className="mr-4 text-right">
              <p className="text-sm text-gray-600">
                <Button
                  variant="inlineLink"
                  onClick={handleResendCode}
                  disabled={resendTimer !== null && resendTimer > 0}
                  className="h-auto p-0 text-sm font-semibold text-blue-600 hover:text-blue-700"
                >
                  {resendTimer !== null && resendTimer > 0
                    ? `Resend in ${moment.utc(resendTimer * 1000).format("m:ss")}`
                    : "Resend code"}
                </Button>
              </p>
            </div>
          </div>

          <AuthSubmitButton loading={isLoading} disabled={!isValid} text="Verify Email" />

          <div className="mx-auto mt-1 w-fit">
            <Button variant="inlineLink" onClick={handleBackToLogin}>
              <ArrowLeftIcon className="mr-1 h-4 w-4" /> Back to Sign In
            </Button>
          </div>
        </form>

        <div className="mt-6 rounded-lg p-4">
          <p className="text-center text-xs text-gray-600">
            Having trouble? Check your spam folder or contact{" "}
            <a
              className="text-blue-600 underline hover:text-blue-700"
              target="_blank"
              href="mailto:hello@promptlayer.com"
            >
              hello@promptlayer.com
            </a>{" "}
            if you need assistance.
          </p>
        </div>
      </div>
    </div>
  );
};
